import _ from 'lodash';
import { ScopeRelatesTo } from '@gtintel/platform-types';
import { SelectionState } from '../context/types';

// return the old node to indicate no change

// Depth-first, post-order tree transforming visitor
// return a new object representing a changed node
// consider the use case, e.g. react before mutating node objects
export const transform = (old, tfn) => {
  let subtreeMutated = false;
  const children = [];
  for (const c of old.children) {
    const n = transform(c, tfn);
    if (n) {
      children.push(n);
    }
    if (!Object.is(n, c)) {
      subtreeMutated = true;
    }
  }
  const neue = tfn(old);
  if (neue === null) return null;
  if (subtreeMutated) {
    return {
      ...neue,
      children
    };
  } else {
    return neue;
  }
};
// Depth-first, post-order tree visitor
export const visit = (n, vfn) => {
  n.children.forEach(c => visit(c, vfn));
  vfn(n);
};
const mutComputeStats = (n, readonly) => {
  for (const c of n.children) {
    mutComputeStats(c, readonly);
  }

  // because it might not be initialized
  n.data.selectionSize = n.data.selectionSize || 0;
  if (n.children.length > 0) {
    n.data.selectionSize = n.children.map(c => c.data.selectionSize || 0).reduce((a, b) => a + b, 0);
  }
  n.data.selectable = n.children.length === 0 ? 1 : n.children.map(c => c.data.selectable).reduce((a, b) => a + b, 0);
  if (readonly) {
    n.children = n.children.filter(c => c.data.selectionSize > 0);
  }
};
export const selectorToPath = selector => {
  const buffer = ['/'];
  if (selector.subject_id) {
    buffer.push('subject', selector.subject_id);
  }
  if (selector.jurisdiction_id) {
    buffer.push('jurisdiction', selector.jurisdiction_id);
  }
  if (selector.scope_component_id) {
    buffer.push('scope', selector.scope_component_id);
  }
  return buffer.join('/');
};
export const selectionToPaths = selection => {
  return selection.reduce((acc, c) => {
    acc[selectorToPath(c)] = c;
    return acc;
  }, {});
};

// mutates!
export const applySelectionState = (state, tree, readonly) => {
  const selectionMap = selectionToPaths(state.selection());
  visit(tree, n => {
    if (n.children.length === 0) {
      const selectionSize = n.path in selectionMap ? 1 : 0;
      n.data.selectionSize = selectionSize;
    }
  });
  mutComputeStats(tree, readonly);
};
export const requestReportTreeFrom = state => {
  const groups = _.groupBy(state.scopeComponents(), 'relates_to');
  const reportComponents = groups[ScopeRelatesTo.Report] || [];
  const subjectComponents = groups[ScopeRelatesTo.Subject] || [];
  const jurisdictionComponents = groups[ScopeRelatesTo.Jurisdiction] || [];
  const project = state.project();
  const fragmentsByPath = _.groupBy(state.fragments(), f => selectorToPath(f));
  const scopeComponentToReportNode = (scopeComponent, parent) => {
    const selector = {
      ...parent,
      scope_component_id: scopeComponent.id
    };
    const path = selectorToPath(selector);
    const fragments = fragmentsByPath[path];
    return {
      path,
      name: scopeComponent.name,
      children: [],
      data: {
        selector,
        instructionsHtml: scopeComponent.description,
        fragments,
        applicability: scopeComponent.applicability,
        is_automated: scopeComponent.is_automated
      }
    };
  };
  const subjectToReportNode = (subject, parent) => {
    const selector = {
      ...parent,
      subject_id: subject.id,
      scope_component_applicability: subject.is_entity ? 'entity' : 'principal'
    };
    const path = selectorToPath(selector);
    const applicability = [selector.scope_component_applicability, 'both'];
    const children = [...subjectComponents.filter(component => applicability.includes(component.applicability)).map(s => scopeComponentToReportNode(s, selector)), ...state.jurisdictionsFor(subject.id).map(j => jurisdictionToReportNode(j, selector)).filter(j => j.children.length > 0)];
    return {
      path,
      name: subject.name,
      children,
      data: {
        selector,
        instructionsHtml: subject.description,
        disabled: children.length === 0
      }
    };
  };
  const jurisdictionToReportNode = (jurisdiction, parent) => {
    const selector = {
      ...parent,
      jurisdiction_id: jurisdiction.id
    };
    const path = selectorToPath(selector);
    const applicability = [selector.scope_component_applicability, 'both'];
    return {
      path,
      name: jurisdiction.name,
      children: jurisdictionComponents.filter(component => applicability.includes(component.applicability)).map(j => scopeComponentToReportNode(j, selector)),
      data: {
        selector
      }
    };
  };
  const rootSelector = {};
  const rootPath = selectorToPath(rootSelector);
  const root = {
    path: rootPath,
    name: project.name,
    children: [...reportComponents.map(r => scopeComponentToReportNode(r, rootSelector)), ...state.subjects().map(s => subjectToReportNode(s, rootSelector))],
    data: {
      selector: rootSelector,
      instructionsHtml: project.instructions_html
    }
  };
  return root;
};
export const selectionState = node => {
  if (node.children.length === 0) {
    return node.data.selectionSize === 1 ? SelectionState.Full : SelectionState.Empty;
  }
  if (node.data.selectable === node.data.selectionSize) {
    return SelectionState.Full;
  }
  if (node.data.selectionSize > 0) {
    return SelectionState.Partial;
  }
  return SelectionState.Empty;
};
export const selectedIn = tree => {
  const selection = transform(tree, n => {
    if (selectionState(n) === SelectionState.Empty) {
      return null;
    }
    return n;
  });
  return selection;
};